import { useEffect, useMemo, useState } from "react";
import React from "react";
import api from "@/lib/api";
import { CoachingEmailsLog } from "@/components/employees/CoachingEmailsLog";
import EmployeeTop25Summary from "@/components/employees/EmployeeTop25Summary";
import EmployeeIncentivesSummary from "@/components/employees/EmployeeIncentivesSummary";
import { toast } from "sonner";
import { useTableSort, useSortState, sortRows, SortTh } from "@/lib/sortable";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { Loader2, Users, AlertCircle, TrendingDown, TrendingUp, Award, FileSpreadsheet, FileDown, FileText, Sparkles, ChevronDown, ChevronRight, X, Mail } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { SendReportDialog } from "@/components/employees/SendReportDialog";

const COLORS = ["#0ea5e9", "#10b981", "#f59e0b", "#e11d48", "#8b5cf6", "#14b8a6", "#f97316", "#ec4899"];
const fmtEur = (n) => Number(n || 0).toLocaleString("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
const fmtPct = (n) => `${Number(n || 0).toFixed(1)}%`;
const fmt0 = (n) => Number(n || 0).toLocaleString("es-ES", { maximumFractionDigits: 0 });

export default function EmployeeAnalysisPage() {
  const [studies, setStudies] = useState([]); // [{id, label, period_start}]
  const [selectedStudy, setSelectedStudy] = useState("");
  const [analysis, setAnalysis] = useState(null); // {employees, has_breakdown}
  const [timeline, setTimeline] = useState(null); // { studies, employees: [{id, name, points: []}] }
  const [loading, setLoading] = useState(true);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [compareA, setCompareA] = useState("");
  const [compareB, setCompareB] = useState("");
  const [scope, setScope] = useState("last"); // "last" | "3m" | "6m" | "12m"
  const [exporting, setExporting] = useState(null);
  const [sharingId, setSharingId] = useState(null);
  const [coachingId, setCoachingId] = useState(null);
  // Iter-81 · Matriz Indicación × Empleado
  const [matrix, setMatrix] = useState(null);
  const [loadingMatrix, setLoadingMatrix] = useState(false);
  const [exportingMatrix, setExportingMatrix] = useState(false);
  // Iter-85 · Drilldown por G.H. dentro de la matriz
  const [ghMatrix, setGhMatrix] = useState(null);

  const [logKey, setLogKey] = useState(0);
  const [bulk, setBulk] = useState(null);
  const [sendTarget, setSendTarget] = useState(null);

  const sendCoachingEmail = (eid, ename, hasEmail) => {
    if (!selectedStudy) return;
    if (!hasEmail) {
      toast.error(`${ename} no tiene email`, {
        description: "Añade su email en la pantalla Personal antes de enviar el coaching.",
        action: { label: "Ir a Personal", onClick: () => { window.location.assign("/empleados"); } },
        duration: 7000,
      });
      return;
    }
    setSendTarget({ mode: "one", eid, ename });
  };

  const doSendOne = async (eid, includeTeam) => {
    setCoachingId(eid);
    try {
      const res = await api.post(`/dispensations/${selectedStudy}/employees/${eid}/coaching-email`, null, { params: { include_team: includeTeam } });
      toast.success(`Email enviado a ${res.data.sent_to}`, {
        description: res.data.attachment
          ? `Adjuntos: ${res.data.attachment}`
          : "Enviado sin adjunto: no se pudo generar el PDF",
        duration: 6500,
      });
      setLogKey((k) => k + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo enviar el coaching");
    } finally {
      setCoachingId(null);
    }
  };

  const sendToAll = () => {
    if (!selectedStudy) return;
    const withEmail = (analysis?.employees || []).filter((e) => !!e.email).length;
    if (!withEmail) {
      toast.error("Ningún empleado tiene email", {
        description: "Añade los emails en la pantalla Personal.",
        duration: 7000,
      });
      return;
    }
    setSendTarget({ mode: "all", count: withEmail });
  };

  const doSendAll = async (includeTeam) => {
    const withEmail = (analysis?.employees || []).filter((e) => !!e.email).length;
    setBulk({ total: withEmail, sent: 0, failed: 0, status: "running" });
    try {
      const { data } = await api.post(`/dispensations/${selectedStudy}/employees/coaching-email-all`, null, { params: { include_team: includeTeam } });
      const poll = setInterval(async () => {
        try {
          const { data: job } = await api.get(`/employees/coaching-email-jobs/${data.job_id}`);
          setBulk(job);
          if (job.status === "done") {
            clearInterval(poll);
            setLogKey((k) => k + 1);
            const failed = (job.results || []).filter((r) => !r.ok);
            if (failed.length) {
              toast.warning(`${job.sent} enviados · ${failed.length} con error`, {
                description: failed.map((f) => `${f.name}: ${f.error}`).join(" · ").slice(0, 200),
                duration: 12000,
              });
            } else {
              toast.success(`Informe enviado a ${job.sent} empleados`, { duration: 8000 });
            }
            setTimeout(() => setBulk(null), 4000);
          }
        } catch {
          clearInterval(poll);
          setBulk(null);
        }
      }, 2500);
    } catch (e) {
      setBulk(null);
      toast.error(e?.response?.data?.detail || "No se pudo iniciar el envío");
    }
  };

  const shareEmployee = async (eid, ename) => {
    if (!selectedStudy) return;
    setSharingId(eid);
    try {
      const res = await api.get(`/dispensations/${selectedStudy}/employees/${eid}/share`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `informe_${ename.replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Informe de ${ename} generado`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al generar el informe");
    } finally {
      setSharingId(null);
    }
  };

  // Carga inicial: lista estudios y timeline
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [disps, tl] = await Promise.all([
          api.get("/dispensations"),
          api.get("/employees/timeline"),
        ]);
        // Incluir estudios donde el timeline reporta empleados con datos, aunque
        // no tengan la flag explícita has_employee_breakdown (estudios legacy).
        const idsWithBreakdown = new Set((tl.data?.studies || []).map((s) => s.id));
        const onlyMatrix = (disps.data || []).filter(
          (d) => d.has_employee_breakdown || idsWithBreakdown.has(d.id),
        );
        setStudies(onlyMatrix);
        setTimeline(tl.data);
        if (onlyMatrix.length > 0) {
          // El backend devuelve `/dispensations` ordenado por period_end DESC
          // → el estudio más reciente es el [0], no el último del array.
          setSelectedStudy(onlyMatrix[0].id);
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || "No se pudo cargar el análisis");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Carga análisis según scope
  useEffect(() => {
    if (scope === "last" && !selectedStudy) {
      setAnalysis(null);
      return;
    }
    (async () => {
      setLoadingAnalysis(true);
      try {
        let data;
        if (scope === "last") {
          const r = await api.get(`/dispensations/${selectedStudy}/employees`);
          data = r.data;
        } else {
          const months = { "3m": 3, "6m": 6, "12m": 12 }[scope];
          const r = await api.get(`/employees/aggregate`, { params: { months } });
          data = r.data;
        }
        setAnalysis(data);
        // Auto-seleccionar dos primeros empleados para la comparativa
        if (data?.employees?.length >= 2) {
          setCompareA(data.employees[0].id);
          setCompareB(data.employees[1].id);
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Error al cargar análisis");
      } finally {
        setLoadingAnalysis(false);
      }
    })();
  }, [selectedStudy, scope]);

  const exportFile = async (format) => {
    setExporting(format);
    try {
      const res = await api.get("/employees/export", {
        params: {
          format,
          scope,
          ...(scope === "last" && selectedStudy ? { disp_id: selectedStudy } : {}),
        },
        responseType: "blob",
      });
      const ext = format === "excel" ? "xlsx" : "pdf";
      const mime = format === "excel"
        ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        : "application/pdf";
      const blob = new Blob([res.data], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `analisis_empleados.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exportado a ${format.toUpperCase()}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al exportar");
    } finally {
      setExporting(null);
    }
  };

  // Iter-81 · Carga matriz Indicación × Empleado para el estudio actual
  useEffect(() => {
    if (scope !== "last" || !selectedStudy) {
      setMatrix(null);
      setGhMatrix(null);
      return;
    }
    (async () => {
      setLoadingMatrix(true);
      try {
        // Iter-85 · pedimos ambas vistas en paralelo (indicación + G.H.) para
        // habilitar el drilldown al hacer clic en una indicación sin ir al backend
        // otra vez.
        const [rInd, rGh] = await Promise.all([
          api.get("/employees/indication-matrix", { params: { disp_id: selectedStudy, group_by: "indication" } }),
          api.get("/employees/indication-matrix", { params: { disp_id: selectedStudy, group_by: "gh" } }),
        ]);
        setMatrix(rInd.data);
        setGhMatrix(rGh.data);
      } catch (e) {
        setMatrix(null);
        setGhMatrix(null);
      } finally {
        setLoadingMatrix(false);
      }
    })();
  }, [selectedStudy, scope]);

  const exportMatrix = async () => {
    if (!selectedStudy) return;
    setExportingMatrix(true);
    try {
      const res = await api.get("/employees/indication-matrix/export", {
        params: { disp_id: selectedStudy },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `matriz_indicacion_empleado.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Matriz exportada a Excel");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al exportar la matriz");
    } finally {
      setExportingMatrix(false);
    }
  };

  // Datos para el chart A: ranking por % genérico (unidades + euros)
  const rankByGeneric = useMemo(() => {
    if (!analysis?.employees) return [];
    return [...analysis.employees]
      .sort((a, b) => b.generic_share_units - a.generic_share_units)
      .map((e) => ({
        name: e.name,
        "% Genérico (uds)": Number((e.generic_share_units || 0).toFixed(1)),
        "% Genérico (€)": Number((e.generic_share_eur || 0).toFixed(1)),
      }));
  }, [analysis]);

  // B: ranking € marca perdida (de más a menos)
  const rankByLoss = useMemo(() => {
    if (!analysis?.employees) return [];
    return [...analysis.employees]
      .sort((a, b) => b.eur_marca_perdida - a.eur_marca_perdida)
      .map((e) => ({ name: e.name, "€ perdidos": Math.round(e.eur_marca_perdida) }));
  }, [analysis]);

  // C: evolución temporal (% genérico unidades + € por mes por empleado)
  const timelineChart = useMemo(() => {
    if (!timeline?.studies?.length) return null;
    // pivot: filas = estudios (label), columnas = "{nombre} · Uds" y "{nombre} · €"
    const rows = timeline.studies.map((s) => {
      const row = { label: s.label || s.period_start };
      timeline.employees.forEach((e) => {
        const p = e.points.find((pp) => pp.disp_id === s.id);
        if (p) {
          row[`${e.name} · Uds`] = p.generic_share_units;
          row[`${e.name} · €`] = p.generic_share_eur;
        }
      });
      return row;
    });
    return rows;
  }, [timeline]);

  // D: cuota de dispensación (donut % unidades del total + % euros del total)
  const lossSort = useSortState();
  const shareDataRaw = useMemo(() => {
    if (!analysis?.employees) return [];
    const totalU = analysis.employees.reduce((s, e) => s + e.units_total, 0) || 1;
    const totalE = analysis.employees.reduce((s, e) => s + (e.eur_total || 0), 0) || 1;
    return analysis.employees.map((e) => ({
      name: e.name,
      value: e.units_total,
      pct: ((e.units_total / totalU) * 100).toFixed(1),
      eur: e.eur_total || 0,
      pctEur: ((((e.eur_total || 0)) / totalE) * 100).toFixed(1),
    }));
  }, [analysis]);
  const shareData = shareDataRaw;
  const shareSort = useTableSort(shareDataRaw, {
    value: (r) => Number(r.value),
    pct: (r) => Number(r.pct),
    eur: (r) => Number(r.eur),
    pctEur: (r) => Number(r.pctEur),
  });

  // F: comparativa A vs B
  const compareData = useMemo(() => {
    if (!analysis?.employees || !compareA || !compareB) return null;
    const a = analysis.employees.find((e) => e.id === compareA);
    const b = analysis.employees.find((e) => e.id === compareB);
    if (!a || !b) return null;
    return { a, b };
  }, [analysis, compareA, compareB]);

  if (loading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto">
        <div className="text-slate-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Cargando…</div>
      </div>
    );
  }

  if (studies.length === 0) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto space-y-6" data-testid="empanalysis-empty">
        {/* Hero banner */}
        <div className="rounded-2xl overflow-hidden bg-slate-950 shadow-xl border border-slate-200">
          <img
            src="/img/farmaclass-employees.jpg"
            alt="FarmaClass · Tu farmacia, tu éxito, nuestro compromiso"
            className="w-full h-[240px] md:h-[280px] object-cover object-center block"
            loading="lazy"
          />
        </div>
        <h1 className="font-display text-4xl font-extrabold tracking-tight mb-2">Análisis por empleado</h1>
        <p className="text-slate-500 mb-8">Análisis automático de la dispensación por miembro del equipo.</p>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 flex gap-4">
          <FileSpreadsheet className="h-8 w-8 text-amber-600 shrink-0" />
          <div>
            <div className="font-bold mb-2">Aún no tienes estudios con desglose por empleado</div>
            <p className="text-sm text-slate-700 mb-3">
              Para activar esta sección, sube un Excel con esta estructura en la cabecera:
            </p>
            <div className="bg-white border border-amber-200 rounded-md p-3 text-xs font-mono mb-3">
              CN | María López | Pedro García | Ana Martín | ...<br/>
              695412 | 10 | 5 | 8 | ...<br/>
              742116 | 3 | 12 | 0 | ...
            </div>
            <p className="text-xs text-slate-600">
              El sistema detectará automáticamente que es un estudio por empleado, creará los nombres en tu personal
              (pendientes de confirmar) y calculará los KPIs por persona.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const total = analysis?.employees?.reduce((s, e) => s + e.units_total, 0) || 0;
  const totalEur = analysis?.employees?.reduce((s, e) => s + e.eur_total, 0) || 0;
  const totalPerdida = analysis?.employees?.reduce((s, e) => s + e.eur_marca_perdida, 0) || 0;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto space-y-6 sm:space-y-8" data-testid="empanalysis-page">
      <SendReportDialog
        target={sendTarget}
        onCancel={() => setSendTarget(null)}
        onConfirm={(inc) => {
          const t = sendTarget;
          setSendTarget(null);
          if (t.mode === "all") doSendAll(inc); else doSendOne(t.eid, inc);
        }}
      />
      {/* Hero banner */}
      <div className="rounded-2xl overflow-hidden bg-slate-950 shadow-xl border border-slate-200" data-testid="empanalysis-hero-image">
        <img
          src="/img/farmaclass-employees.jpg"
          alt="FarmaClass · Tu farmacia, tu éxito, nuestro compromiso"
          className="w-full h-[140px] sm:h-[200px] md:h-[280px] object-cover object-center block"
          loading="lazy"
        />
      </div>

      {/* Header */}
      <div className="space-y-4">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">Personal · Analítica</div>
            <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight">Análisis por empleado</h1>
            <p className="text-slate-500 mt-1 text-xs sm:text-sm">Estudios con desglose por miembro del equipo.</p>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            {scope === "last" && (
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Estudio</span>
                <select
                  value={selectedStudy}
                  onChange={(e) => setSelectedStudy(e.target.value)}
                  className="mt-1 border border-slate-300 rounded-md px-3 py-2 text-sm bg-white"
                  data-testid="empanalysis-study"
                >
                  {studies.map((s) => (
                    <option key={s.id} value={s.id}>{s.label || s.period_start}</option>
                  ))}
                </select>
              </label>
            )}
            <div className="inline-flex gap-1">
              <button
                onClick={sendToAll}
                disabled={!!bulk || !analysis?.employees?.length}
                className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white text-xs font-bold px-3 py-2 rounded-md"
                data-testid="send-all-coaching"
                title="Enviar a cada empleado su informe en PDF y sus recomendaciones de IA"
              >
                {bulk ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}
                {bulk ? `${bulk.sent + bulk.failed}/${bulk.total}` : "Enviar a todos"}
              </button>
              <button
                onClick={() => exportFile("excel")}
                disabled={exporting || !analysis?.employees?.length}
                className="inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white text-xs font-bold px-3 py-2 rounded-md"
                data-testid="export-excel"
                title="Exportar a Excel"
              >
                {exporting === "excel" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileSpreadsheet className="h-3.5 w-3.5" />} Excel
              </button>
              <button
                onClick={() => exportFile("pdf")}
                disabled={exporting || !analysis?.employees?.length}
                className="inline-flex items-center gap-1.5 bg-rose-500 hover:bg-rose-600 disabled:opacity-50 text-white text-xs font-bold px-3 py-2 rounded-md"
                data-testid="export-pdf"
                title="Exportar a PDF"
              >
                {exporting === "pdf" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />} PDF
              </button>
            </div>
          </div>
        </div>

        {/* Scope tabs */}
        <div className="inline-flex bg-slate-100 rounded-full p-1 gap-1 text-xs font-bold" data-testid="scope-tabs">
          {[
            { id: "last", label: "Estudio actual" },
            { id: "3m", label: "Últimos 3 meses" },
            { id: "6m", label: "Últimos 6 meses" },
            { id: "12m", label: "Últimos 12 meses" },
          ].map((s) => (
            <button
              key={s.id}
              onClick={() => setScope(s.id)}
              className={`px-3.5 py-1.5 rounded-full transition-colors ${
                scope === s.id ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"
              }`}
              data-testid={`scope-${s.id}`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {scope !== "last" && analysis?.studies?.length > 0 && (
          <div className="text-xs text-slate-500">
            <FileDown className="inline h-3 w-3 mr-1" />
            Agrega <strong>{analysis.studies.length}</strong> estudio{analysis.studies.length !== 1 ? "s" : ""}:{" "}
            {analysis.studies.map((s) => s.label || s.period_start).join(" · ")}
          </div>
        )}
      </div>

      {loadingAnalysis ? (
        <div className="text-slate-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Calculando…</div>
      ) : !analysis?.employees?.length ? (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-8 text-center">
          <Users className="h-8 w-8 mx-auto text-slate-300 mb-2" />
          <div className="font-display text-lg font-bold">Sin datos por empleado en este estudio</div>
        </div>
      ) : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard label="Empleados activos" value={analysis.employees.length} accent="sky" Icon={Users} />
            <KpiCard label="Unidades totales" value={fmt0(total)} accent="slate" />
            <KpiCard label="Facturación" value={fmtEur(totalEur)} accent="emerald" />
            <KpiCard label="€ marca perdidos" value={fmtEur(totalPerdida)} accent="rose" Icon={TrendingDown} />
          </div>

          {/* Iter-112 · Resumen del Top 25 por empleado (quién mejora en lo que importa) */}
          {scope === "last" && selectedStudy && (
            <Section
              title="Top 25 por empleado"
              subtitle="Mide a cada empleado SOLO en las 25 moléculas del Top: % de genérico, diferencia con la media del equipo y mejora respecto al estudio anterior."
            >
              <EmployeeTop25Summary dispId={selectedStudy} />
            </Section>
          )}

          {/* Iter-144 · Copia del resumen de incentivos (si el programa está en marcha) */}
          {scope === "last" && <EmployeeIncentivesSummary dispId={selectedStudy} />}

          <CoachingEmailsLog refreshKey={logKey} />

          {/* CHART A: ranking por % genérico (barras agrupadas: uds vs €) */}
          <Section title="A · Ranking por % genérico" subtitle="Verde = % en unidades · Azul = % en euros. Cuanto más alto y equilibrado, mejor mezcla EFG.">
            <ResponsiveContainer width="100%" height={Math.max(240, analysis.employees.length * 46)}>
              <BarChart data={rankByGeneric} layout="vertical" margin={{ top: 8, right: 32, left: 8, bottom: 8 }} barGap={2}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" domain={[0, 100]} tickFormatter={(v) => `${v}%`} stroke="#64748b" fontSize={12} />
                <YAxis type="category" dataKey="name" width={140} stroke="#64748b" fontSize={12} />
                <Tooltip formatter={(v) => `${Number(v).toFixed(1)}%`} />
                <Legend />
                <Bar dataKey="% Genérico (uds)" fill="#10b981" />
                <Bar dataKey="% Genérico (€)" fill="#0ea5e9" />
              </BarChart>
            </ResponsiveContainer>
          </Section>

          {/* CHART B: ranking € marca perdidos */}
          <Section title="B · Ranking de € marca perdidos" subtitle="Marca que dispensó cada empleado teniendo genérico disponible">
            <ResponsiveContainer width="100%" height={Math.max(220, analysis.employees.length * 38)}>
              <BarChart data={rankByLoss} layout="vertical" margin={{ top: 8, right: 32, left: 8, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" stroke="#64748b" fontSize={12} tickFormatter={(v) => fmtEur(v)} />
                <YAxis type="category" dataKey="name" width={140} stroke="#64748b" fontSize={12} />
                <Tooltip formatter={(v) => fmtEur(v)} />
                <Bar dataKey="€ perdidos" fill="#e11d48" />
              </BarChart>
            </ResponsiveContainer>
          </Section>

          {/* CHART D: donut cuota */}
          <Section title="D · Cuota de dispensación" subtitle="Qué porcentaje del total dispensa cada empleado">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={shareData} dataKey="value" nameKey="name" innerRadius={60} outerRadius={110} paddingAngle={2}>
                    {shareData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v, n, ent) => [`${fmt0(v)} uds (${ent.payload.pct}% uds · ${ent.payload.pctEur}% €)`, ent.payload.name]} />
                </PieChart>
              </ResponsiveContainer>
              <div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                      <SortTh field="name" sort={shareSort} className="py-2">Empleado</SortTh>
                      <SortTh field="value" sort={shareSort} align="right" className="py-2">Uds</SortTh>
                      <SortTh field="pct" sort={shareSort} align="right" className="py-2">Cuota uds</SortTh>
                      <SortTh field="eur" sort={shareSort} align="right" className="py-2">€</SortTh>
                      <SortTh field="pctEur" sort={shareSort} align="right" className="py-2">Cuota €</SortTh>
                    </tr>
                  </thead>
                  <tbody>
                    {shareSort.sorted.map((s) => (
                      <tr key={s.name} className="border-b border-slate-100">
                        <td className="py-2 flex items-center gap-2">
                          <span className="h-2.5 w-2.5 rounded-full" style={{ background: COLORS[Math.max(0, shareData.findIndex((x) => x.name === s.name)) % COLORS.length] }} />
                          {s.name}
                        </td>
                        <td className="py-2 text-right tabular-nums">{fmt0(s.value)}</td>
                        <td className="py-2 text-right tabular-nums font-semibold text-emerald-700">{s.pct}%</td>
                        <td className="py-2 text-right tabular-nums">{fmtEur(s.eur)}</td>
                        <td className="py-2 text-right tabular-nums font-semibold text-sky-700">{s.pctEur}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </Section>

          {/* CHART F: A vs B comparativa */}
          <Section title="F · Comparativa A vs B" subtitle="Selecciona dos empleados y compara sus KPIs lado a lado">
            <div className="grid grid-cols-2 gap-4 mb-6">
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Empleado A</span>
                <select value={compareA} onChange={(e) => setCompareA(e.target.value)} className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm bg-white" data-testid="cmp-a">
                  <option value="">—</option>
                  {analysis.employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Empleado B</span>
                <select value={compareB} onChange={(e) => setCompareB(e.target.value)} className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm bg-white" data-testid="cmp-b">
                  <option value="">—</option>
                  {analysis.employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </label>
            </div>
            {compareData && <CompareTable a={compareData.a} b={compareData.b} />}
          </Section>

          {/* Top perdidas + top dispensa por empleado */}
          <Section title="Donde cada empleado pierde más y dispensa más">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {analysis.employees.map((e) => {
                const tlEmp = timeline?.employees?.find((te) => te.id === e.id);
                return (
                <div key={e.id} className="border border-slate-200 rounded-xl p-5">
                  <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-display text-lg font-bold">{e.name}</div>
                      {e.pending && (
                        <span className="text-[9px] uppercase tracking-wider font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Pendiente</span>
                      )}
                      {tlEmp && tlEmp.points && tlEmp.points.length >= 2 && (
                        <EmpSparkline points={tlEmp.points} />
                      )}
                    </div>
                    {scope === "last" && selectedStudy && (
                      <div className="inline-flex items-center gap-1.5">
                        <button
                          onClick={() => sendCoachingEmail(e.id, e.name, !!e.email)}
                          disabled={!!coachingId || !!sharingId}
                          className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-3 py-1 rounded-full transition-colors ${
                            e.email
                              ? "bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white"
                              : "bg-amber-100 text-amber-800 hover:bg-amber-200"
                          } disabled:opacity-50`}
                          data-testid={`coaching-emp-${e.id}`}
                          title={e.email ? `Enviar coaching IA a ${e.email}` : "Empleado sin email — añádelo en Personal"}
                        >
                          {coachingId === e.id
                            ? <Loader2 className="h-3 w-3 animate-spin" />
                            : <Sparkles className="h-3 w-3" />}
                          {e.email ? "Coaching IA" : "+ Email"}
                        </button>
                        <button
                          onClick={() => shareEmployee(e.id, e.name)}
                          disabled={!!sharingId || !!coachingId}
                          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white px-3 py-1 rounded-full"
                          data-testid={`share-emp-${e.id}`}
                          title="Generar PDF personal del empleado"
                        >
                          {sharingId === e.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileDown className="h-3 w-3" />}
                          Compartir
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4 text-center">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">% Gen. uds</div>
                      <div className="font-display text-xl font-extrabold text-emerald-600">{fmtPct(e.generic_share_units)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">% Gen. €</div>
                      <div className="font-display text-xl font-extrabold text-sky-600">{fmtPct(e.generic_share_eur)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">€ Total</div>
                      <div className="font-display text-xl font-extrabold">{fmtEur(e.eur_total)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">€ Perdidos</div>
                      <div className="font-display text-xl font-extrabold text-rose-600">{fmtEur(e.eur_marca_perdida)}</div>
                    </div>
                  </div>

                  <div className="text-[10px] uppercase tracking-wider text-rose-600 font-bold mb-1 inline-flex items-center gap-1">
                    <TrendingDown className="h-3 w-3" /> Top 10 moléculas donde más pierde
                  </div>
                  {e.top_perdidas.length === 0 ? (
                    <div className="text-xs text-slate-400 italic mb-3">No hay marca con genérico disponible.</div>
                  ) : (
                    <table className="w-full text-[11px] mb-3 border border-slate-200 rounded">
                      <thead className="text-[9px] uppercase tracking-wider text-slate-500 bg-slate-50">
                        <tr>
                          <SortTh field="name" sort={lossSort} className="px-2 py-1 font-semibold">Molécula</SortTh>
                          <SortTh field="eur" sort={lossSort} align="right" className="px-1 py-1 font-semibold">€ pierde</SortTh>
                          <SortTh field="emp_gen_pct" sort={lossSort} align="right" className="px-1 py-1 font-semibold">Tu %</SortTh>
                          <SortTh field="team_gen_pct" sort={lossSort} align="right" className="px-1 py-1 font-semibold bg-amber-100/60 text-amber-800">μ farm.</SortTh>
                          <SortTh field="delta_vs_team" sort={lossSort} align="right" className="px-1 py-1 font-semibold">Δ</SortTh>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {sortRows(e.top_perdidas.slice(0, 10), lossSort.sortField, lossSort.sortDir).map((t) => {
                          const empPct = Number(t.emp_gen_pct ?? 0);
                          const teamPct = Number(t.team_gen_pct ?? 0);
                          const delta = Number(t.delta_vs_team ?? 0);
                          const dColor = delta > 0.5 ? "text-emerald-600" : delta < -0.5 ? "text-rose-600" : "text-slate-500";
                          const dArrow = delta > 0.5 ? "▲" : delta < -0.5 ? "▼" : "=";
                          return (
                            <tr key={t.gh || t.cn} title={t.gh}>
                              <td className="px-2 py-1 truncate max-w-[180px] text-slate-700">{t.name || t.gh || t.cn}</td>
                              <td className="px-1 py-1 text-right tabular-nums font-semibold text-rose-600">{fmtEur(t.eur)}</td>
                              <td className="px-1 py-1 text-right tabular-nums">{empPct.toFixed(1)}%</td>
                              <td className="px-1 py-1 text-right tabular-nums bg-amber-50 text-amber-900">{teamPct.toFixed(1)}%</td>
                              <td className={`px-1 py-1 text-right tabular-nums font-bold ${dColor}`}>
                                {delta >= 0 ? "+" : ""}{delta.toFixed(1)}pp <span className="text-[9px]">{dArrow}</span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}

                  <div className="text-[10px] uppercase tracking-wider text-sky-600 font-bold mb-1 inline-flex items-center gap-1">
                    <Award className="h-3 w-3" /> Donde más dispensa
                  </div>
                  <ul className="text-xs space-y-0.5">
                    {e.top_dispensed.slice(0, 5).map((t) => (
                      <li key={t.cn} className="flex justify-between gap-2 py-0.5">
                        <span className="truncate text-slate-700">{t.name || t.cn}</span>
                        <span className="tabular-nums font-semibold text-slate-900 shrink-0">{fmt0(t.units)} uds</span>
                      </li>
                    ))}
                  </ul>
                </div>
                );
              })}
            </div>
          </Section>
        </>
      )}

      {/* CHART C: evolución temporal — % genérico uds (línea sólida) y % genérico € (línea discontinua) */}
      {timelineChart && timelineChart.length > 1 && (
        <Section title="C · Evolución mensual del % genérico por empleado" subtitle="Línea sólida = % en unidades · Línea discontinua = % en euros. Líneas más altas = mejor mezcla EFG.">
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={timelineChart} margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="label" stroke="#64748b" fontSize={12} />
              <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} stroke="#64748b" fontSize={12} />
              <Tooltip formatter={(v) => `${Number(v).toFixed(1)}%`} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {timeline.employees.map((e, i) => {
                // Iter-60: color personalizado del empleado si lo asignó, o
                // paleta estándar como fallback (mismo comportamiento anterior).
                const color = (e.color && /^#[0-9A-Fa-f]{6}$/.test(e.color)) ? e.color : COLORS[i % COLORS.length];
                return [
                  <Line
                    key={`${e.id}-uds`}
                    type="monotone"
                    dataKey={`${e.name} · Uds`}
                    stroke={color}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />,
                  <Line
                    key={`${e.id}-eur`}
                    type="monotone"
                    dataKey={`${e.name} · €`}
                    stroke={color}
                    strokeWidth={1.5}
                    strokeDasharray="5 4"
                    dot={{ r: 2 }}
                  />,
                ];
              })}
            </LineChart>
          </ResponsiveContainer>
        </Section>
      )}

      {/* Iter-81 · Matriz Indicación × Empleado */}
      {scope === "last" && (
        <IndicationEmployeeMatrix
          data={matrix}
          ghData={ghMatrix}
          loading={loadingMatrix}
          exporting={exportingMatrix}
          onExport={exportMatrix}
        />
      )}

      {/* Iter-82 · Coaching por indicación */}
      {scope === "last" && selectedStudy && (
        <IndicationCoachingPanel dispId={selectedStudy} />
      )}
    </div>
  );
}

// Iter-81 · Componente: Matriz Indicación × Empleado (+ drill-down por G.H. iter-85 + drill-down por empleado iter-86)
function IndicationEmployeeMatrix({ data, ghData, loading, exporting, onExport }) {
  const [expandedInd, setExpandedInd] = useState(null); // ind.short o null
  const [drilldownEmp, setDrilldownEmp] = useState(null); // empleado seleccionado para drawer

  if (loading) {
    return (
      <section className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="section-matrix-loading">
        <div className="text-slate-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Cargando matriz…</div>
      </section>
    );
  }
  if (!data || !data.has_breakdown) {
    return null;
  }
  const employees = data.employees || [];
  const indications = data.indications || [];
  const cells = data.cells || {};
  const totInd = data.totals_by_indication || {};
  const totEmp = data.totals_by_employee || {};
  if (indications.length === 0 || employees.length === 0) return null;

  // Iter-85 · Índice GH → cells para drill-down. Filtramos GHs por `family`
  // (que en group_by=gh contiene el nombre de la indicación) en el momento
  // de renderizar la fila expandida.
  const ghRows = ghData?.indications || [];
  const ghCells = ghData?.cells || {};
  const ghTotals = ghData?.totals_by_indication || {};
  const ghsByIndication = new Map();
  for (const r of ghRows) {
    const indKey = r.family || "Sin clasificar";
    if (!ghsByIndication.has(indKey)) ghsByIndication.set(indKey, []);
    ghsByIndication.get(indKey).push(r);
  }

  const pctColor = (pct, opp) => {
    if (!opp || opp <= 0) return "bg-slate-50 text-slate-300";
    if (pct >= 70) return "bg-emerald-50 text-emerald-700";
    if (pct >= 40) return "bg-amber-50 text-amber-700";
    return "bg-rose-50 text-rose-700";
  };
  const colSpanTotal = 1 + employees.length + 1;

  return (
    <section className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="indication-employee-matrix">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <h2 className="font-display text-xl font-bold tracking-tight">Matriz Indicación × Empleado</h2>
          <p className="text-slate-500 text-sm">
            % cambio a genérico por cada empleado en cada familia terapéutica del estudio. Verde ≥70% · Ámbar 40-70% · Rojo &lt;40%.
            <br />
            <span className="text-[11px]">Entre paréntesis: uds genérico / oportunidades totales. Clic en la fila para ver las moléculas del grupo.</span>
          </p>
        </div>
        <button
          onClick={onExport}
          disabled={exporting}
          className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-bold px-3 py-2 rounded-md"
          data-testid="export-matrix-xlsx"
          title="Descargar la matriz en Excel"
        >
          {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileSpreadsheet className="h-3.5 w-3.5" />}
          Excel matriz
        </button>
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-200" data-testid="matrix-scroll">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 sticky top-0">
            <tr>
              <th className="text-left px-3 py-2 font-bold text-slate-700 border-r border-slate-200 min-w-[220px] sticky left-0 bg-slate-50 z-10">
                Indicación
              </th>
              {employees.map((e) => (
                <th
                  key={e.id}
                  className="text-center px-2 py-2 font-bold text-slate-700 border-r border-slate-100 min-w-[110px] cursor-pointer hover:bg-slate-100 transition-colors"
                  data-testid={`matrix-col-${e.id}`}
                  title={`Clic para ver el desglose de ${e.name}`}
                  onClick={() => setDrilldownEmp(e)}
                >
                  <div className="flex items-center justify-center gap-1.5">
                    <span
                      className="h-2.5 w-2.5 rounded-full border border-slate-300"
                      style={{ background: e.color || "repeating-conic-gradient(#e2e8f0 0 25%, #ffffff 0 50%) 50% 50% / 6px 6px" }}
                    />
                    <span className="truncate max-w-[90px]" title={e.name}>{e.name}</span>
                  </div>
                </th>
              ))}
              <th className="text-center px-2 py-2 font-bold text-slate-700 bg-slate-100 min-w-[100px]">Total fila</th>
            </tr>
          </thead>
          <tbody>
            {indications.map((ind) => {
              const key = ind.short;
              const tot = totInd[key] || {};
              const isExpanded = expandedInd === key;
              const molecules = ghsByIndication.get(key) || [];
              return (
                <React.Fragment key={key}>
                  <tr
                    className={`border-t border-slate-100 cursor-pointer hover:bg-slate-50/70 ${isExpanded ? "bg-violet-50/40" : ""}`}
                    data-testid={`matrix-row-${key.replace(/\s+/g, "-")}`}
                    onClick={() => setExpandedInd(isExpanded ? null : key)}
                    title="Clic para ver las moléculas de este grupo"
                  >
                    <td className="text-left px-3 py-2 border-r border-slate-200 sticky left-0 bg-inherit z-10">
                      <div className="flex items-center gap-1.5">
                        {isExpanded ? <ChevronDown className="h-3 w-3 text-violet-600 shrink-0" /> : <ChevronRight className="h-3 w-3 text-slate-400 shrink-0" />}
                        <div className="min-w-0">
                          <div className="font-semibold text-slate-800 leading-tight">{key}</div>
                          {ind.family && (
                            <div className="text-[10px] text-slate-400 uppercase tracking-wider">{ind.family}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    {employees.map((e) => {
                      const cell = (cells[key] || {})[e.id];
                      const opp = cell?.opportunities || 0;
                      const pct = cell?.pct_change ?? 0;
                      return (
                        <td
                          key={e.id}
                          className={`text-center px-2 py-2 border-r border-slate-100 tabular-nums font-mono-num ${pctColor(pct, opp)}`}
                          title={opp > 0 ? `${e.name} · ${key}\n${cell.units_generic} genérico / ${opp} oportunidades\n(${cell.units_marca} marca · pct ${pct.toFixed(1)}%)` : "Sin dispensación"}
                          data-testid={`matrix-cell-${e.id}-${key.replace(/\s+/g, "-")}`}
                        >
                          {opp > 0 ? (
                            <>
                              <div className="font-bold">{pct.toFixed(0)}%</div>
                              <div className="text-[9px] opacity-70">{cell.units_generic}/{opp}</div>
                            </>
                          ) : (
                            <span className="text-slate-300">—</span>
                          )}
                        </td>
                      );
                    })}
                    <td className="text-center px-2 py-2 bg-slate-50 tabular-nums font-mono-num font-bold">
                      <div>{(tot.pct_change ?? 0).toFixed(0)}%</div>
                      <div className="text-[9px] opacity-70 font-normal">{tot.units_generic}/{tot.opportunities}</div>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="bg-violet-50/30 border-t border-violet-200" data-testid={`matrix-drilldown-${key.replace(/\s+/g, "-")}`}>
                      <td colSpan={colSpanTotal} className="p-0">
                        {molecules.length === 0 ? (
                          <div className="px-4 py-3 text-slate-500 text-[11px] italic">
                            Sin moléculas con volumen en este grupo para este estudio.
                          </div>
                        ) : (
                          <table className="w-full text-[11px]">
                            <thead>
                              <tr className="text-[9px] uppercase tracking-wider text-violet-700 bg-violet-100/60 border-b border-violet-200">
                                <th className="text-left px-4 py-1.5 font-semibold min-w-[220px] sticky left-0 bg-violet-100/60 z-[5]">
                                  ↳ Molécula (G.H.)
                                </th>
                                {employees.map((e) => (
                                  <th key={e.id} className="text-center px-2 py-1.5 font-semibold min-w-[110px]">
                                    {e.name}
                                  </th>
                                ))}
                                <th className="text-center px-2 py-1.5 font-semibold bg-violet-100 min-w-[100px]">Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {molecules.map((mol) => {
                                const molKey = mol.short;
                                const molTot = ghTotals[molKey] || {};
                                return (
                                  <tr key={molKey} className="border-b border-violet-100 last:border-0" data-testid={`matrix-mol-${molKey.replace(/\s+/g, "-")}`}>
                                    <td className="text-left px-4 py-1.5 sticky left-0 bg-violet-50 z-[5]">
                                      <div className="font-medium text-slate-800 leading-tight truncate max-w-[200px]" title={molKey}>{molKey}</div>
                                    </td>
                                    {employees.map((e) => {
                                      const cell = (ghCells[molKey] || {})[e.id];
                                      const opp = cell?.opportunities || 0;
                                      const pct = cell?.pct_change ?? 0;
                                      return (
                                        <td
                                          key={e.id}
                                          className={`text-center px-2 py-1.5 tabular-nums font-mono-num ${pctColor(pct, opp)}`}
                                          title={opp > 0 ? `${e.name} · ${molKey}\n${cell.units_generic}/${opp}` : "Sin dispensación"}
                                        >
                                          {opp > 0 ? (
                                            <>
                                              <div className="font-bold">{pct.toFixed(0)}%</div>
                                              <div className="text-[9px] opacity-70">{cell.units_generic}/{opp}</div>
                                            </>
                                          ) : (
                                            <span className="text-slate-300">—</span>
                                          )}
                                        </td>
                                      );
                                    })}
                                    <td className="text-center px-2 py-1.5 tabular-nums font-mono-num font-bold bg-violet-100/40">
                                      <div>{(molTot.pct_change ?? 0).toFixed(0)}%</div>
                                      <div className="text-[9px] opacity-70 font-normal">{molTot.units_generic}/{molTot.opportunities}</div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
          <tfoot className="bg-slate-100 sticky bottom-0">
            <tr>
              <td className="text-left px-3 py-2 font-bold text-slate-800 border-r border-slate-200 sticky left-0 bg-slate-100 z-10">
                Total empleado
              </td>
              {employees.map((e) => {
                const tot = totEmp[e.id] || {};
                return (
                  <td
                    key={e.id}
                    className="text-center px-2 py-2 font-bold tabular-nums font-mono-num border-r border-slate-200 cursor-pointer hover:bg-slate-200 transition-colors"
                    data-testid={`matrix-total-emp-${e.id}`}
                    onClick={() => setDrilldownEmp(e)}
                    title={`Clic para ver el desglose de ${e.name}`}
                  >
                    <div>{(tot.pct_change ?? 0).toFixed(0)}%</div>
                    <div className="text-[9px] opacity-70 font-normal">{tot.units_generic}/{tot.opportunities}</div>
                  </td>
                );
              })}
              <td className="text-center px-2 py-2 bg-slate-200"></td>
            </tr>
          </tfoot>
        </table>
      </div>
      <div className="text-[11px] text-slate-500 mt-2">
        Detecta specialistas: quien saca notas altas en una indicación concreta puede orientar al resto del equipo en esa familia.
      </div>

      {/* Iter-86 · Drilldown por empleado */}
      <EmployeeDrilldownDrawer
        employee={drilldownEmp}
        onClose={() => setDrilldownEmp(null)}
        indications={indications}
        cells={cells}
        totInd={totInd}
        totEmp={totEmp}
        ghData={ghData}
      />
    </section>
  );
}

// Iter-86 · Drawer: desglose por empleado (indicaciones + moléculas de esa persona)
function EmployeeDrilldownDrawer({ employee, onClose, indications, cells, totInd, totEmp, ghData }) {
  const [expandedInd, setExpandedInd] = useState(null);
  useEffect(() => {
    setExpandedInd(null);
  }, [employee?.id]);

  if (!employee) return null;
  const eid = employee.id;
  const empTotal = totEmp[eid] || { units_marca: 0, units_generic: 0, opportunities: 0, pct_change: 0 };

  // Filas de indicación con datos de este empleado, ordenadas por oportunidades
  const empRows = indications
    .map((ind) => {
      const cell = (cells[ind.short] || {})[eid];
      return cell && cell.opportunities > 0 ? { ind, cell } : null;
    })
    .filter(Boolean)
    .sort((a, b) => b.cell.opportunities - a.cell.opportunities);

  // Índice moléculas por indicación (usa la matriz por G.H.)
  const ghRows = ghData?.indications || [];
  const ghCells = ghData?.cells || {};
  const ghsByIndication = new Map();
  for (const r of ghRows) {
    const indKey = r.family || "Sin clasificar";
    const cell = (ghCells[r.short] || {})[eid];
    if (cell && cell.opportunities > 0) {
      if (!ghsByIndication.has(indKey)) ghsByIndication.set(indKey, []);
      ghsByIndication.get(indKey).push({ gh: r.short, cell });
    }
  }

  const pctColor = (pct) => {
    if (pct >= 70) return "text-emerald-700 bg-emerald-50";
    if (pct >= 40) return "text-amber-700 bg-amber-50";
    return "text-rose-700 bg-rose-50";
  };

  return (
    <Sheet open={!!employee} onOpenChange={(open) => { if (!open) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="employee-drilldown-drawer">
        <SheetHeader className="text-left space-y-2 mb-4">
          <div className="flex items-center gap-3">
            <span
              className="h-6 w-6 rounded-full border-2 border-slate-300 shrink-0"
              style={{ background: employee.color || "repeating-conic-gradient(#e2e8f0 0 25%, #ffffff 0 50%) 50% 50% / 10px 10px" }}
            />
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.22em] text-violet-600 font-bold">Desglose por empleado</div>
              <SheetTitle className="font-display text-2xl font-extrabold tracking-tight">{employee.name}</SheetTitle>
            </div>
          </div>
          <SheetDescription className="text-sm text-slate-500">
            % cambio por indicación y molécula en el estudio actual. Verde ≥70% · Ámbar 40-70% · Rojo &lt;40%.
          </SheetDescription>
        </SheetHeader>

        {/* KPIs empleado */}
        <div className="grid grid-cols-4 gap-2 mb-5">
          <KPI label="% Cambio" value={`${empTotal.pct_change.toFixed(1)}%`} tone={empTotal.pct_change >= 70 ? "green" : empTotal.pct_change >= 40 ? "amber" : "rose"} />
          <KPI label="Oportunidades" value={empTotal.opportunities} tone="slate" />
          <KPI label="Uds marca" value={empTotal.units_marca} tone="rose" />
          <KPI label="Uds gen." value={empTotal.units_generic} tone="emerald" />
        </div>

        {empRows.length === 0 ? (
          <div className="text-slate-500 text-sm italic">Sin dispensaciones asignadas a este empleado en el estudio.</div>
        ) : (
          <div className="space-y-1.5" data-testid="employee-drilldown-list">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2 flex items-center justify-between">
              <span>{empRows.length} indicaciones con actividad</span>
              <span>Clic para ver moléculas</span>
            </div>
            {empRows.map(({ ind, cell }) => {
              const key = ind.short;
              const isOpen = expandedInd === key;
              const mols = (ghsByIndication.get(key) || []).sort((a, b) => b.cell.opportunities - a.cell.opportunities);
              return (
                <div key={key} className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`emp-drilldown-ind-${key.replace(/\s+/g, "-")}`}>
                  <button
                    className="w-full flex items-center gap-3 px-3 py-2 hover:bg-slate-50 text-left"
                    onClick={() => setExpandedInd(isOpen ? null : key)}
                  >
                    {isOpen ? <ChevronDown className="h-3.5 w-3.5 text-violet-600 shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 text-slate-400 shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm text-slate-900 truncate">{key}</div>
                      {ind.family && (
                        <div className="text-[10px] text-slate-400 uppercase tracking-wider">{ind.family}</div>
                      )}
                    </div>
                    <div className={`px-2 py-1 rounded-md font-mono-num tabular-nums text-xs font-bold ${pctColor(cell.pct_change)}`}>
                      {cell.pct_change.toFixed(0)}%
                    </div>
                    <div className="text-[10px] text-slate-500 tabular-nums font-mono-num w-16 text-right">
                      {cell.units_generic}/{cell.opportunities}
                    </div>
                  </button>
                  {isOpen && (
                    <div className="border-t border-slate-100 bg-violet-50/30">
                      {mols.length === 0 ? (
                        <div className="px-4 py-2 text-slate-500 text-[11px] italic">Sin moléculas con volumen para este empleado en este grupo.</div>
                      ) : (
                        <table className="w-full text-[11px]">
                          <tbody>
                            {mols.map(({ gh, cell: mc }) => (
                              <tr key={gh} className="border-t border-violet-100 first:border-0" data-testid={`emp-drilldown-mol-${gh.replace(/\s+/g, "-")}`}>
                                <td className="px-4 py-1.5 text-slate-800 truncate max-w-[300px]" title={gh}>{gh}</td>
                                <td className="px-2 py-1.5 text-right tabular-nums text-rose-700">{mc.units_marca}</td>
                                <td className="px-2 py-1.5 text-right tabular-nums text-emerald-700 font-semibold">{mc.units_generic}</td>
                                <td className="px-2 py-1.5 text-right tabular-nums text-slate-500">{mc.opportunities}</td>
                                <td className={`px-3 py-1.5 text-right tabular-nums font-mono-num font-bold ${pctColor(mc.pct_change)}`}>
                                  {mc.pct_change.toFixed(0)}%
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function KPI({ label, value, tone = "slate" }) {
  const toneCls = {
    green: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    rose: "bg-rose-50 text-rose-700 border-rose-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    slate: "bg-slate-50 text-slate-700 border-slate-200",
  }[tone] || "bg-slate-50 text-slate-700 border-slate-200";
  return (
    <div className={`border rounded-lg px-3 py-2 ${toneCls}`}>
      <div className="text-[9px] uppercase tracking-wider font-bold opacity-80">{label}</div>
      <div className="font-display text-lg font-extrabold tabular-nums font-mono-num">{value}</div>
    </div>
  );
}

// Iter-82 · Componente: Coaching por Indicación
function IndicationCoachingPanel({ dispId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [minOpp, setMinOpp] = useState(5);
  const [minGap, setMinGap] = useState(15);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  // Iter-88 · Plantilla de coaching semanal en PDF (una hoja por especialista + anexo)
  const downloadCoachingPdf = async () => {
    setDownloadingPdf(true);
    toast.info("Generando plantillas de coaching (incluye mensajes IA)…");
    try {
      const res = await api.get("/employees/indication-coaching/export-pdf", {
        params: { disp_id: dispId, min_opp: minOpp, min_gap_pp: minGap },
        responseType: "blob",
        timeout: 180000,
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "coaching_semanal.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Plantillas de coaching descargadas");
    } catch (e) {
      toast.error("No se pudo generar el PDF de coaching");
    } finally {
      setDownloadingPdf(false);
    }
  };

  useEffect(() => {
    if (!dispId) return;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/employees/indication-coaching", {
          params: { disp_id: dispId, min_opp: minOpp, min_gap_pp: minGap },
        });
        setData(r.data);
      } catch (e) {
        setData(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [dispId, minOpp, minGap]);

  if (loading) {
    return (
      <section className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="section-coaching-loading">
        <div className="text-slate-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Analizando coaching…</div>
      </section>
    );
  }
  if (!data || !data.has_breakdown) return null;
  const indications = data.indications || [];
  if (indications.length === 0) {
    return (
      <section className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="indication-coaching-empty">
        <h2 className="font-display text-xl font-bold tracking-tight">Coaching por indicación</h2>
        <p className="text-slate-500 text-sm mt-1">
          No detectamos hoy diferencias significativas entre empleados en ninguna familia terapéutica.
          Prueba a bajar el gap mínimo o reducir el volumen mínimo.
        </p>
      </section>
    );
  }

  return (
    <section className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="indication-coaching">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.22em] text-violet-600 font-bold flex items-center gap-1.5">
            <Sparkles className="h-3 w-3" /> Coaching · Próximo turno
          </div>
          <h2 className="font-display text-xl font-bold tracking-tight">Coaching por indicación</h2>
          <p className="text-slate-500 text-sm">
            Detectamos a la <strong>especialista</strong> de cada familia y las compañeras con
            mayor margen de mejora. Objetivo del próximo turno: replicar su técnica.
          </p>
        </div>
        <div className="flex items-end gap-3">
          <label className="block">
            <span className="text-[9px] uppercase tracking-wider text-slate-500 font-bold">Vol. mín. (uds)</span>
            <input
              type="number"
              min={1}
              value={minOpp}
              onChange={(e) => setMinOpp(Math.max(1, parseInt(e.target.value, 10) || 1))}
              className="mt-1 w-20 border border-slate-300 rounded-md px-2 py-1.5 text-sm bg-white"
              data-testid="coaching-min-opp"
            />
          </label>
          <label className="block">
            <span className="text-[9px] uppercase tracking-wider text-slate-500 font-bold">Gap mín. (pp)</span>
            <input
              type="number"
              min={0}
              value={minGap}
              onChange={(e) => setMinGap(Math.max(0, parseInt(e.target.value, 10) || 0))}
              className="mt-1 w-20 border border-slate-300 rounded-md px-2 py-1.5 text-sm bg-white"
              data-testid="coaching-min-gap"
            />
          </label>
          <button
            onClick={downloadCoachingPdf}
            disabled={downloadingPdf}
            className="inline-flex items-center gap-2 bg-violet-600 hover:bg-violet-700 disabled:opacity-60 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
            data-testid="coaching-download-pdf"
            title="Una hoja por especialista con las compañeras a entrenar, guion y mensaje IA"
          >
            {downloadingPdf ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
            Plantillas de coaching
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="coaching-cards-grid">
        {indications.map((ind) => (
          <div
            key={ind.indication}
            className="border border-violet-200 bg-gradient-to-br from-violet-50 via-white to-white rounded-xl p-5"
            data-testid={`coaching-card-${ind.indication.replace(/\s+/g, "-")}`}
          >
            <div className="flex items-start justify-between gap-2 mb-3">
              <div className="min-w-0">
                <div className="font-display text-lg font-bold text-slate-900 leading-tight">{ind.indication}</div>
                {ind.family && (
                  <div className="text-[10px] uppercase tracking-wider text-violet-600 font-semibold mt-0.5">{ind.family}</div>
                )}
              </div>
              <div className="text-right shrink-0">
                <div className="text-[9px] uppercase tracking-wider text-slate-500 font-bold">Impacto</div>
                <div className="font-display text-xl font-extrabold text-emerald-600 tabular-nums">
                  +{ind.potential_units_to_convert}
                </div>
                <div className="text-[9px] text-slate-500">uds convertibles</div>
              </div>
            </div>

            {/* Specialist */}
            <div className="bg-emerald-100/60 border border-emerald-200 rounded-lg p-3 mb-3" data-testid={`coaching-specialist-${ind.indication.replace(/\s+/g, "-")}`}>
              <div className="flex items-center gap-2 mb-1">
                <Award className="h-4 w-4 text-emerald-600 shrink-0" />
                <span className="text-[9px] uppercase tracking-wider text-emerald-700 font-bold">Especialista</span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <div className="font-bold text-slate-900 truncate">{ind.specialist.name}</div>
                <div className="text-right tabular-nums shrink-0">
                  <div className="font-mono-num font-bold text-emerald-700">{ind.specialist.pct_change.toFixed(1)}%</div>
                  <div className="text-[9px] text-slate-500">{ind.specialist.units_generic}/{ind.specialist.opportunities}</div>
                </div>
              </div>
            </div>

            {/* Candidates */}
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1.5 flex items-center gap-1">
              <TrendingUp className="h-3 w-3 text-amber-600" /> A entrenar con {ind.specialist.name.split(" ")[0]} · {ind.n_candidates}
            </div>
            <ul className="space-y-1.5">
              {ind.candidates.slice(0, 4).map((c) => (
                <li
                  key={c.employee_id}
                  className="flex items-center justify-between gap-2 bg-white border border-slate-100 rounded-md px-3 py-1.5"
                  data-testid={`coaching-candidate-${c.employee_id}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className="h-2.5 w-2.5 rounded-full border border-slate-300 shrink-0"
                      style={{ background: c.color || "repeating-conic-gradient(#e2e8f0 0 25%, #ffffff 0 50%) 50% 50% / 6px 6px" }}
                    />
                    <span className="text-sm font-semibold text-slate-800 truncate">{c.name}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs font-mono-num text-slate-600">{c.pct_change.toFixed(0)}%</span>
                    <span className="inline-flex items-center gap-0.5 text-[10px] font-mono-num text-rose-600 font-bold" title={`Está ${c.delta_pp}pp por debajo del specialist`}>
                      <TrendingDown className="h-2.5 w-2.5" />
                      −{c.delta_pp.toFixed(0)}pp
                    </span>
                  </div>
                </li>
              ))}
            </ul>
            {ind.candidates.length > 4 && (
              <div className="text-[10px] text-slate-500 mt-1.5 pl-1">+ {ind.candidates.length - 4} más</div>
            )}
          </div>
        ))}
      </div>
      <div className="text-[11px] text-slate-500 mt-3">
        Ordenado por <strong>impacto</strong>: primero las indicaciones donde más unidades podrías convertir a genérico si el resto igualara al specialist.
      </div>
    </section>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <section className="bg-white border border-slate-200 rounded-2xl p-6" data-testid={`section-${title.split(" ")[0].toLowerCase()}`}>
      <h2 className="font-display text-xl font-bold tracking-tight">{title}</h2>
      {subtitle && <p className="text-slate-500 text-sm mb-4">{subtitle}</p>}
      <div className="mt-2">{children}</div>
    </section>
  );
}

function KpiCard({ label, value, accent = "slate", Icon }) {
  const c = {
    sky: "text-sky-600",
    emerald: "text-emerald-600",
    rose: "text-rose-600",
    slate: "text-slate-900",
  }[accent];
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold flex items-center gap-1">
        {Icon && <Icon className="h-3 w-3" />}
        {label}
      </div>
      <div className={`font-display text-2xl font-extrabold mt-1 ${c}`}>{value}</div>
    </div>
  );
}

function CompareTable({ a, b }) {
  const rows = [
    ["% Genérico (unidades)", fmtPct(a.generic_share_units), fmtPct(b.generic_share_units), "up"],
    ["% Marca (unidades)", fmtPct(a.brand_share_units), fmtPct(b.brand_share_units), "down"],
    ["% Genérico (€)", fmtPct(a.generic_share_eur), fmtPct(b.generic_share_eur), "up"],
    ["Unidades totales", fmt0(a.units_total), fmt0(b.units_total), "neutral"],
    ["Unidades marca", fmt0(a.units_marca), fmt0(b.units_marca), "down"],
    ["Unidades genérico", fmt0(a.units_generico), fmt0(b.units_generico), "up"],
    ["€ totales", fmtEur(a.eur_total), fmtEur(b.eur_total), "neutral"],
    ["€ marca perdidos", fmtEur(a.eur_marca_perdida), fmtEur(b.eur_marca_perdida), "down"],
  ];
  return (
    <table className="w-full text-sm" data-testid="cmp-table">
      <thead>
        <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b-2 border-slate-200">
          <th className="text-left py-3">KPI</th>
          <th className="text-right py-3">{a.name}</th>
          <th className="text-right py-3">{b.name}</th>
          <th className="text-right py-3">Δ</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(([label, av, bv, dir]) => {
          // Comparison logic on raw values
          const numA = parseFloat(String(av).replace(/[^\d,.-]/g, "").replace(",", "."));
          const numB = parseFloat(String(bv).replace(/[^\d,.-]/g, "").replace(",", "."));
          const winA = dir === "up" ? numA > numB : dir === "down" ? numA < numB : null;
          return (
            <tr key={label} className="border-b border-slate-100">
              <td className="py-2.5 text-slate-700">{label}</td>
              <td className={`py-2.5 text-right tabular-nums ${winA === true ? "font-bold text-emerald-600" : winA === false ? "text-slate-500" : "text-slate-900"}`}>{av}</td>
              <td className={`py-2.5 text-right tabular-nums ${winA === false ? "font-bold text-emerald-600" : winA === true ? "text-slate-500" : "text-slate-900"}`}>{bv}</td>
              <td className="py-2.5 text-right tabular-nums text-xs text-slate-500">
                {!isNaN(numA) && !isNaN(numB) && (
                  <span className={numA - numB > 0 ? "text-sky-600" : numA - numB < 0 ? "text-rose-600" : ""}>
                    {numA - numB > 0 ? <TrendingUp className="inline h-3 w-3" /> : numA - numB < 0 ? <TrendingDown className="inline h-3 w-3" /> : null}
                    {" "}{(numA - numB).toFixed(numA % 1 !== 0 ? 1 : 0)}
                  </span>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}


/**
 * Mini sparkline SVG con los últimos 3-6 puntos de % genérico (€) del empleado.
 * Muestra una flecha ▲/▼ con el delta total para identificar tendencias de un vistazo.
 */
function EmpSparkline({ points, metric = "generic_share_eur" }) {
  if (!points || points.length < 2) return null;
  const slice = points.slice(-6);
  const values = slice.map((p) => Number(p[metric] || 0));
  const min = Math.min(...values, 0);
  const max = Math.max(...values, 100);
  const range = Math.max(max - min, 1);
  const w = 70;
  const h = 22;
  const stepX = w / Math.max(1, slice.length - 1);
  const pts = values
    .map((v, i) => `${(i * stepX).toFixed(1)},${(h - ((v - min) / range) * h).toFixed(1)}`)
    .join(" ");
  const first = values[0];
  const last = values[values.length - 1];
  const delta = last - first;
  const color = delta > 0.5 ? "#10b981" : delta < -0.5 ? "#e11d48" : "#64748b";
  const arrow = delta > 0.5 ? "▲" : delta < -0.5 ? "▼" : "→";
  return (
    <span
      className="inline-flex items-center gap-1.5 text-[10px] font-bold tabular-nums"
      style={{ color }}
      title={`Tendencia últimos ${slice.length} estudios (% Gen €). ${first.toFixed(1)}% → ${last.toFixed(1)}% (Δ ${delta >= 0 ? "+" : ""}${delta.toFixed(1)}pp)`}
      data-testid="emp-sparkline"
    >
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
        <polyline
          points={pts}
          fill="none"
          stroke={color}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {values.map((v, i) => (
          <circle key={i} cx={(i * stepX).toFixed(1)} cy={(h - ((v - min) / range) * h).toFixed(1)} r={i === values.length - 1 ? 2 : 1.3} fill={color} />
        ))}
      </svg>
      <span aria-hidden>{arrow}</span>
      <span>{delta >= 0 ? "+" : ""}{delta.toFixed(1)}pp</span>
    </span>
  );
}

